import { useEffect, useRef, useState } from 'react';
import { FaCoins } from 'react-icons/fa';
import SiteHeader from '../components/SiteHeader.jsx';
import SiteFooter from '../components/SiteFooter.jsx';
import { useCoins } from '../hooks/useCoins.jsx';
import { useAds } from '../context/AdContext.jsx';
import { useSeo } from '../hooks/useSeo.js';
import { GAMES, ORDER } from '../games/miniGames.js';

const REVIVE_COST = 50;

export default function MiniGamesPage() {
  useSeo(
    'Mini-Game Hub — 10 Free Games | QuizzyLoot',
    'Play 10 fun hyper-casual mini-games on QuizzyLoot — tap, whack, build and pop to earn coins. Free to play.',
  );
  const { coins, addCoins } = useCoins();
  const { showInterstitial, showRewarded } = useAds();

  const [view, setView] = useState('hub'); // 'hub' | 'game'
  const [gameId, setGameId] = useState(null);
  const [started, setStarted] = useState(false); // false = showing How-to-Play
  const [score, setScore] = useState(0);
  const [earned, setEarned] = useState(0);
  const [failOpen, setFailOpen] = useState(false);
  const [summary, setSummary] = useState(null); // { earned } | null
  const [doubled, setDoubled] = useState(false);

  const areaRef = useRef(null);
  const gameRef = useRef(null);
  const earnedRef = useRef(0);
  const resumeRef = useRef(null);
  const finishRef = useRef(null);

  // Stable api handed to each game.
  const apiRef = useRef(null);
  if (!apiRef.current) {
    apiRef.current = {
      setScore: (n) => setScore(n),
      addEarned: (n) => { earnedRef.current += n; setEarned(earnedRef.current); },
      getEarned: () => earnedRef.current,
      fail: (resume) => { resumeRef.current = resume; setFailOpen(true); },
      win: () => finishRef.current && finishRef.current(),
    };
  }

  function stopGame() { if (gameRef.current && gameRef.current.stop) { try { gameRef.current.stop(); } catch (e) {} } }

  // Credit the run's coins to the wallet and show the summary.
  function finishRun() {
    stopGame();
    addCoins(earnedRef.current);
    setDoubled(false);
    setSummary({ earned: earnedRef.current });
  }
  finishRef.current = finishRun;

  // Mount / unmount the active game — only once the player taps "Start" on the
  // How-to-Play screen.
  useEffect(() => {
    if (view !== 'game' || !gameId || !started) return;
    const area = areaRef.current;
    if (!area) return;
    area.innerHTML = '';
    earnedRef.current = 0; setEarned(0); setScore(0);
    const ctrl = GAMES[gameId].make(area, apiRef.current);
    gameRef.current = ctrl;
    ctrl.start && ctrl.start();
    // No innerHTML clear here: React removes the keyed game <section> (and the
    // canvas with it) on exit. Clearing here could wipe a reused hub node.
    return () => { try { ctrl.stop && ctrl.stop(); } catch (e) {} };
  }, [view, gameId, started]);

  // Interstitial fires right before any game loop initializes.
  const launch = (id) => { showInterstitial().then(() => { setSummary(null); setFailOpen(false); setStarted(false); setGameId(id); setView('game'); }); };

  const reviveWithCoins = () => {
    if (coins < REVIVE_COST) return;
    addCoins(-REVIVE_COST);
    setFailOpen(false);
    resumeRef.current && resumeRef.current();
  };
  const reviveWithAd = () => {
    setFailOpen(false);
    showRewarded().then(({ viewed }) => { if (viewed) resumeRef.current && resumeRef.current(); else setFailOpen(true); });
  };
  const exitAndCollect = () => { setFailOpen(false); finishRun(); };

  const doubleRewards = () => {
    if (doubled || !summary || summary.earned <= 0) return;
    showRewarded().then(({ viewed }) => { if (viewed) { addCoins(summary.earned); setDoubled(true); } });
  };
  // Return to the hub (no interstitial here — the one shown on game launch is
  // enough; two ads bracketing a short game is a poor experience).
  const backToHub = () => { setSummary(null); setGameId(null); setView('hub'); };

  const game = gameId ? GAMES[gameId] : null;

  return (
    <div className="min-h-screen flex flex-col page-in">
      <SiteHeader variant="light" />

      <main className="flex-1">
        {view === 'hub' ? (
          <section key="hub" className="max-w-site mx-auto px-4 sm:px-6 py-8">
            <div className="text-center mb-7 reveal">
              <h1 className="text-3xl sm:text-4xl font-black">Choose a <span className="text-gradient-brand">Mini-Game</span></h1>
              <p className="mt-2 text-slate-500 text-sm">10 quick games · earn coins · revive &amp; double your rewards.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 reveal">
              {ORDER.map((id, n) => (
                <button
                  key={id}
                  onClick={() => launch(id)}
                  className="btn-3d surface-card rounded-3xl p-4 flex flex-col items-center text-center gap-2 hover:-translate-y-1 text-slate-900"
                >
                  <span className="text-4xl">{GAMES[id].icon}</span>
                  <span className="font-black text-sm leading-tight">{GAMES[id].name}</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-brand">Play #{n + 1}</span>
                </button>
              ))}
            </div>
          </section>
        ) : (
          <section key="game" className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <button onClick={exitAndCollect} className="btn-3d flex items-center gap-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 px-3 py-2 text-xs font-black uppercase text-slate-700">← Exit</button>
              <h2 className="font-black uppercase tracking-wide text-sm sm:text-base truncate text-slate-900">{game && game.name}</h2>
              <div className="flex items-center gap-2 sm:gap-3 text-xs font-black">
                {started && (
                  <button onClick={() => setStarted(false)} aria-label="How to play"
                    className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-700">?</button>
                )}
                <span className="text-slate-500">Score <span className="text-slate-900">{score}</span></span>
                <span className="rounded-full bg-amber-brand/15 text-amber-600 px-2.5 py-1 flex items-center gap-1"><FaCoins />{earned}</span>
              </div>
            </div>
            <div className="relative w-full" style={{ height: 'min(70vh,560px)' }}>
              <div ref={areaRef} className="dark-stage rounded-3xl relative overflow-hidden w-full h-full" />

              {/* How to Play overlay (shown before the game starts) */}
              {!started && game && (
                <div className="absolute inset-0 rounded-3xl bg-slate-950/85 backdrop-blur-md flex flex-col items-center justify-center text-center p-6 pop-in">
                  <div className="text-5xl mb-2">{game.icon}</div>
                  <h3 className="text-xl font-black uppercase tracking-wide text-white">How to Play</h3>
                  <p className="text-xs text-amber-300 font-bold mb-4">{game.name}</p>
                  <ul className="text-left space-y-2 max-w-xs w-full mb-6">
                    {(game.how || [game.hint]).map((step, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-200">
                        <span className="shrink-0 w-5 h-5 rounded-full bg-violet-600 text-white text-[10px] font-black flex items-center justify-center mt-0.5">{i + 1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => setStarted(true)}
                    className="btn-3d w-full max-w-xs py-3.5 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-600 text-white font-black uppercase tracking-widest text-sm">
                    ▶ Start Game
                  </button>
                </div>
              )}
            </div>
            <p className="text-center text-[11px] text-slate-500 mt-3">{game && game.hint}</p>
          </section>
        )}
      </main>

      {view === 'hub' && <SiteFooter />}

      {/* FAIL MODAL */}
      {failOpen && (
        <div className="fade-in fixed inset-0 z-[80] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-6">
          <div className="glass-panel rounded-[2rem] w-full max-w-sm p-7 text-center pop-in">
            <div className="text-6xl mb-2">💥</div>
            <h3 className="text-2xl font-black uppercase text-rose-400">Game Over</h3>
            <p className="text-slate-300 text-sm mt-1 flex items-center justify-center gap-1.5">Score <span className="font-black text-white">{score}</span> · Earned <span className="text-amber-300 font-black inline-flex items-center gap-1"><FaCoins className="text-amber-400" />{earned}</span></p>
            <div className="mt-6 space-y-3">
              <button onClick={reviveWithCoins} disabled={coins < REVIVE_COST}
                className="btn-3d w-full py-3.5 rounded-2xl bg-violet-600 hover:bg-violet-500 font-black uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-40">
                <FaCoins className="text-amber-300" /> Spend {REVIVE_COST} Coins to Revive
              </button>
              <button onClick={reviveWithAd}
                className="btn-3d relative w-full py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 font-black uppercase text-sm">
                <span className="absolute top-1.5 right-3 text-[8px] text-white/70 font-bold">AD</span>🎬 Watch Ad to Revive
              </button>
              <button onClick={exitAndCollect}
                className="btn-3d w-full py-3.5 rounded-2xl bg-white/10 hover:bg-white/15 font-black uppercase text-sm">🏁 Exit &amp; Collect</button>
            </div>
          </div>
        </div>
      )}

      {/* SUMMARY MODAL */}
      {summary && (
        <div className="fade-in fixed inset-0 z-[80] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-6">
          <div className="glass-panel rounded-[2rem] w-full max-w-sm p-7 text-center pop-in">
            <div className="text-6xl mb-2">🏆</div>
            <h3 className="text-2xl font-black uppercase text-amber-300">Run Complete!</h3>
            <p className="text-slate-300 text-sm mt-1">You collected</p>
            <p className="text-5xl font-black text-amber-300 tabular-nums my-2 flex items-center justify-center gap-2"><FaCoins className="text-amber-400" /> {summary.earned}</p>
            {doubled && <p className="text-emerald-400 font-bold text-sm mb-2">Rewards doubled! 🎉</p>}
            <div className="mt-4 space-y-3">
              {summary.earned > 0 && !doubled && (
                <button onClick={doubleRewards}
                  className="btn-3d w-full py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 font-black uppercase text-sm flex items-center justify-center gap-2">
                  <span>📺 Double Rewards</span>
                  <span className="text-[9px] font-bold bg-black/30 rounded px-1.5 py-0.5 tracking-normal normal-case">Watch Ad</span>
                </button>
              )}
              <button onClick={backToHub}
                className="btn-3d w-full py-3.5 rounded-2xl bg-violet-600 hover:bg-violet-500 font-black uppercase text-sm">Back to Hub →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
